import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContent, Spinner, Button, ButtonIcon, Row, Pagination, Toggle } from 'components';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import { formatDate } from 'helpers/date';
import {
  loadIpmaMeetingApplicationsCreator,
  setIpmaMeetingApplicationsPageCreator,
  setIpmaMeetingApplicationsSortCreator,
  setIpmaMeetingApplicationsFiltersCreator,
  setIpmaMeetingApplicationVerifiedCreator,
} from 'store/actionsCreators';
import { describeSubmitterShort } from 'containers/ma/Meetings/Application/submitter';
import { flagKeys, flagColumnKeys } from 'containers/ma/Meetings/Application/flag';
import MeetingsTable, { ApplicantCell } from 'containers/ma/Meetings/Meetings.styled';
import CisContainer from '../../../../components/CisContainer/CisContainer.styled';

const LIST_TYPE = 'attendance';

/**
 * The attendance forms of the green profile: who of IPMA comes to which of the
 * open meetings.
 *
 * Everybody with an IPMA role fills one in, so the screen always offers the way
 * into the reader's own form. The table below it holds every form for the
 * people that may read them, and only the reader's own for everybody else - the
 * API decides which of the two it hands over.
 */
const AttendanceList = ({
  loading,
  list,
  loadList,
  setPage,
  setSort,
  setFilters,
  setVerified,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);

  useEffect(() => {
    if (document.getElementById('ipma-meetings-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [sort]);

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    setSort(direction ? `${key}.${direction}` : null);
  };

  const statusOptions = [
    { value: 'SUBMITTED', label: t('page.ma.meetings.list.statusSubmitted') },
    { value: 'ACCEPTED', label: t('page.ma.meetings.list.statusAccepted') },
    { value: 'REJECTED', label: t('page.ma.meetings.list.statusRejected') },
  ];

  // An attendance form is never invoiced, so the whole column is the verified
  // one - rows and heading alike.
  const columnFlag = flagColumnKeys(LIST_TYPE);
  const flagOptions = [
    { value: 'PAID', label: t(columnFlag.filterOn) },
    { value: 'UNPAID', label: t(columnFlag.filterOff) },
  ];

  const canApprove = checkPermissions(
    { roles: ['ipma_meeting_approve_ipma'] },
    userPermissions || [],
  );

  return (
    <PageContent>
      <CisContainer>
        <MeetingsTable tablebreakpoint="lg" actionsWidth="10rem">
          <MeetingsTable.Head>
            <MeetingsTable.Row>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.applicant || ''}
                  onFilter={(val) => handleColumnFilterChange('applicant', val)}
                  filterPlaceholder={t('page.ipma.meetings.attendance.applicantPlaceholder')}
                  label={t('page.ipma.meetings.list.applicant')}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  label={t('page.ipma.meetings.attendance.submittedAt')}
                  sort
                  sortDirection={getSortDirection('submittedAt')}
                  onSort={(dir) => handleColumnSort('submittedAt', dir)}
                  isAction
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.status || ''}
                  onFilter={(val) => handleColumnFilterChange('status', val)}
                  filterOptions={statusOptions}
                  filterPlaceholder={t('page.ma.meetings.list.statusPlaceholder')}
                  label={t('page.ma.meetings.list.status')}
                  sort
                  sortDirection={getSortDirection('status')}
                  onSort={(dir) => handleColumnSort('status', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.verified || ''}
                  onFilter={(val) => handleColumnFilterChange('verified', val)}
                  filterOptions={flagOptions}
                  filterPlaceholder={t(columnFlag.placeholder)}
                  label={t(columnFlag.column)}
                  sort
                  sortDirection={getSortDirection('verified')}
                  onSort={(dir) => handleColumnSort('verified', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t('page.ma.meetings.list.actions')} isAction />
              </MeetingsTable.Heading>
            </MeetingsTable.Row>
          </MeetingsTable.Head>
          <MeetingsTable.Body>
            {loading ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={5}>
                  <Spinner />
                </MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={5}>{t('global.noResultsFound')}</MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <MeetingsTable.Row key={item.id}>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ipma.meetings.list.applicant')}:
                      </MeetingsTable.Label>
                      <ApplicantCell title={describeSubmitterShort(item.submittedBy) || undefined}>
                        {describeSubmitterShort(item.submittedBy) ||
                          (item.applicant ? item.applicant.name : '-')}
                      </ApplicantCell>
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ipma.meetings.attendance.submittedAt')}:
                      </MeetingsTable.Label>
                      {item.submittedAt ? formatDate(item.submittedAt) : '-'}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ma.meetings.list.status')}:
                      </MeetingsTable.Label>
                      {item.statusLabel}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>{t(columnFlag.column)}:</MeetingsTable.Label>
                      {/* The flag is the step after the decision, so it stays
                          shut - and says why - until IPMA accepted the
                          application. */}
                      {canApprove ? (
                        <Toggle
                          on={!!item.verified}
                          disabled={!item.verifiable}
                          title={
                            item.verifiable ? undefined : t('page.ipma.meetings.list.verifyBlocked')
                          }
                          custom={[t(flagKeys(item.flag).on), t(flagKeys(item.flag).off)]}
                          onClick={() =>
                            setVerified({
                              applicationId: item.id,
                              meetingType: LIST_TYPE,
                              verified: !item.verified,
                            })
                          }
                        />
                      ) : (
                        t(item.verified ? flagKeys(item.flag).on : flagKeys(item.flag).off)
                      )}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ma.meetings.list.actions')}:
                      </MeetingsTable.Label>
                      <Row style={{ gap: '3px', justifyContent: 'center' }}>
                        <ButtonIcon
                          icon="eye"
                          secondary
                          as={Button}
                          title={t('page.ipma.meetings.list.view')}
                          onClick={() =>
                            history.push(`/ipma/meetings/attendance/application/${item.id}`)
                          }
                        />
                      </Row>
                    </MeetingsTable.Cell>
                  </MeetingsTable.Row>
                ))
              : null}
          </MeetingsTable.Body>
        </MeetingsTable>
        <Pagination
          styled
          currentPage={page}
          totalPages={pages}
          totalItems={totalItems}
          perPage={perPage}
          onChange={(payload) => {
            setPage(payload);
            loadList();
          }}
        />
      </CisContainer>
      {portalReady &&
        ReactDOM.createPortal(
          <Button
            style={{ width: 'auto' }}
            onClick={() => history.push('/ipma/meetings/attendance/form')}
          >
            {t('page.ipma.meetings.attendance.myForm')}
          </Button>,
          document.getElementById('ipma-meetings-buttons-portal'),
        )}
    </PageContent>
  );
};

AttendanceList.propTypes = {
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  setVerified: PropTypes.func.isRequired,
};

AttendanceList.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ipma, user }) => ({
  loading: isLoading(loading, 'GLOBAL'),
  list: ipma.meetings[LIST_TYPE],
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadIpmaMeetingApplicationsCreator(LIST_TYPE)),
  setPage: (payload) => dispatch(setIpmaMeetingApplicationsPageCreator(LIST_TYPE, payload)),
  setSort: (payload) => dispatch(setIpmaMeetingApplicationsSortCreator(LIST_TYPE, payload)),
  setFilters: (payload) => dispatch(setIpmaMeetingApplicationsFiltersCreator(LIST_TYPE, payload)),
  setVerified: (payload) => dispatch(setIpmaMeetingApplicationVerifiedCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AttendanceList);
